import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import axios from 'axios';
import { config } from '../config';
import PaymentSuccess from './PaymentSuccess';
import './StripePayment.css';

// Initialize Stripe
console.log('🔑 Stripe publishable key:', config.STRIPE_PUBLISHABLE_KEY);
const stripePromise = loadStripe(config.STRIPE_PUBLISHABLE_KEY);

interface PaymentFormProps {
  amount: number;
  description: string;
  metadata?: Record<string, any>;
  onSuccess: (paymentIntent: any) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
}

interface PaymentIntent {
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  amount,
  description,
  metadata,
  onSuccess,
  onError,
  onCancel
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    // Create payment intent when component mounts
    createPaymentIntent();
  }, []);

  console.log('🔍 CheckoutForm render - clientSecret:', clientSecret, 'loading:', loading, 'error:', error);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      console.log('🚀 NEW STRIPE COMPONENT LOADED - Creating payment intent with:', { amount, description, metadata });
      console.log('🔗 Backend URL:', config.BACKEND_URL);
      console.log('⏰ Timestamp:', new Date().toISOString());
      
      const response = await axios.post(`${config.BACKEND_URL}/api/stripe/create-payment-intent-simple?cache_bust=${Date.now()}`, {
        amount,
        description,
        metadata,
        debug: "NEW_ENDPOINT_CALLED"
      });

      console.log('Payment intent response:', response.data);

      if (response.data.success) {
        console.log('🔑 Setting client secret:', response.data.client_secret);
        setClientSecret(response.data.client_secret);
      } else {
        console.log('❌ Payment intent creation failed');
        onError('Failed to create payment intent');
      }
    } catch (err: any) {
      console.error('Payment intent error:', err);
      onError(err.response?.data?.detail || 'Failed to create payment intent');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError('Card element not found');
      setLoading(false);
      return;
    }

    const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      }
    });

    if (stripeError) {
      setError(stripeError.message || 'Payment failed');
      setLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      console.log('🎉 Payment succeeded in PaymentForm:', paymentIntent);
      onSuccess(paymentIntent);
    }
  };

  if (loading && !clientSecret) {
    return (
      <div className="payment-loading">
        <div className="spinner"></div>
        <p>Setting up payment...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="payment-form">
      <div className="payment-details">
        <h3>Payment Details</h3>
        <p><strong>Amount:</strong> ${amount.toFixed(2)} CAD</p>
        <p><strong>Description:</strong> {description}</p>
      </div>

      <div className="card-element-container">
        <label>Card Information</label>
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': {
                  color: '#aab7c4',
                },
              },
              invalid: {
                color: '#9e2146',
              },
            },
            hidePostalCode: true
          }}
        />
      </div>

      <div className="postal-code-container">
        <label>Postal Code (Canadian Format)</label>
        <input
          type="text"
          placeholder="K1A 0A6"
          pattern="[A-Za-z][0-9][A-Za-z] [0-9][A-Za-z][0-9]"
          title="Enter a valid Canadian postal code (e.g., K1A 0A6)"
          className="postal-code-input"
          required
        />
        <small className="postal-code-help">Format: A1A 1A1 (letters and numbers)</small>
      </div>

      {error && (
        <div className="payment-error">
          {error}
        </div>
      )}

      <div className="payment-actions">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!stripe || loading}
        >
          {loading ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
};

interface StripePaymentProps {
  amount: number;
  description: string;
  metadata?: Record<string, any>;
  onSuccess: (paymentIntent: any) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
  show: boolean;
  onBackToDashboard?: () => void;
  paymentSuccess?: any;
}

const StripePayment: React.FC<StripePaymentProps> = ({
  amount,
  description,
  metadata,
  onSuccess,
  onError,
  onCancel,
  show,
  onBackToDashboard,
  paymentSuccess: propPaymentSuccess
}) => {
  const [internalPaymentSuccess, setInternalPaymentSuccess] = useState<any>(null);
  const [hasShownSuccess, setHasShownSuccess] = useState(false);
  
  // Use prop paymentSuccess if available, otherwise use internal state
  const paymentSuccess = propPaymentSuccess || internalPaymentSuccess;
  
  console.log('🔄 StripePayment render - show:', show, 'propPaymentSuccess:', propPaymentSuccess, 'internalPaymentSuccess:', internalPaymentSuccess, 'final paymentSuccess:', paymentSuccess);
  console.log('🔄 paymentSuccess type:', typeof paymentSuccess, 'is null:', paymentSuccess === null, 'is undefined:', paymentSuccess === undefined);
  console.log('🔄 paymentSuccess truthy:', !!paymentSuccess);

  // Track component lifecycle
  useEffect(() => {
    console.log('🟢 StripePayment Mounted');
    return () => {
      console.log('🔴 StripePayment Unmounted');
    };
  }, []);

  // Track paymentSuccess state changes
  useEffect(() => {
    console.log('✨ StripePayment useEffect - paymentSuccess changed to:', paymentSuccess);
    console.log('✨ paymentSuccess type:', typeof paymentSuccess, 'is null:', paymentSuccess === null, 'is undefined:', paymentSuccess === undefined);
    console.log('✨ paymentSuccess truthy:', !!paymentSuccess);
  }, [paymentSuccess]);

  const handlePaymentSuccess = (paymentIntent: any) => {
    console.log('🎉 handlePaymentSuccess called with:', paymentIntent);
    console.log('🎉 About to call setInternalPaymentSuccess with:', paymentIntent);
    setInternalPaymentSuccess(paymentIntent);
    setHasShownSuccess(true);
    console.log('🎉 setInternalPaymentSuccess called, component should re-render');
    onSuccess(paymentIntent);
  };

  const handleBackToDashboard = () => {
    console.log('🏠 handleBackToDashboard called');
    setInternalPaymentSuccess(null);
    if (onBackToDashboard) {
      onBackToDashboard();
    }
  };

  const handleMakeAnotherPayment = () => {
    setInternalPaymentSuccess(null);
    setHasShownSuccess(false);
  };

  // Show payment success screen FIRST (even if show is false, we want to show success)
  console.log('🔍 Checking paymentSuccess condition - paymentSuccess:', paymentSuccess, 'truthy:', !!paymentSuccess, 'hasShownSuccess:', hasShownSuccess);
  if (paymentSuccess || hasShownSuccess) {
    console.log('🎉 Rendering PaymentSuccess component with:', paymentSuccess);
    return (
      <div className="stripe-payment-modal">
        <PaymentSuccess
          paymentIntent={paymentSuccess}
          onBackToDashboard={handleBackToDashboard}
          onMakeAnotherPayment={handleMakeAnotherPayment}
        />
      </div>
    );
  } else {
    console.log('❌ paymentSuccess is falsy, not rendering success page');
  }

  // Only return null if show is false AND we don't have a successful payment
  if (!show) return null;

  return (
    <div className="stripe-payment-modal">
      <div className="stripe-payment-content">
        <div className="stripe-payment-header">
          <h2>💳 Secure Payment</h2>
          <p>Powered by Stripe</p>
        </div>

        <Elements stripe={stripePromise} options={{
          locale: 'en-CA'
        }}>
          <PaymentForm
            amount={amount}
            description={description}
            metadata={metadata}
            onSuccess={handlePaymentSuccess}
            onError={onError}
            onCancel={onCancel}
          />
        </Elements>

        <div className="stripe-payment-footer">
          <div className="security-badges">
            <span className="security-badge">🔒 SSL Secured</span>
            <span className="security-badge">🛡️ PCI Compliant</span>
            <span className="security-badge">✅ Stripe Protected</span>
          </div>
          <p className="stripe-disclaimer">
            Your payment information is processed securely by Stripe. 
            We never store your card details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StripePayment;
